import { Box, Button, Flex, Text } from "@chakra-ui/react";
import { useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Trans, useTranslation } from "react-i18next";
import { Avatar } from "./user/Avatar";
import { useAccountData } from "../hooks/useAccountData";
import { TextField } from "./fields/TextField";
import { apiClient } from "../services/axiosInstance";
import { FunnelComponentProps, FunnelContextArea } from "./funnel/types";
import axios, { AxiosResponse } from "axios";
import { Account, ServerResponse, ServerSingleResponse } from "../types";
import { Link } from "react-router-dom";
import { GET_ACCOUNT_ME_QUERY_KEY } from "./auth/api/authApi";
import { CloseCircleIcon } from "./icons/CloseCircleIcon";
import { CloseIcon } from "@chakra-ui/icons";

// API function for username validation
async function postUsername(
  username: string,
): Promise<AxiosResponse<ServerSingleResponse<Account>, any>> {
  return apiClient.post("/v1/account/username", {
    meta: {},
    payload: { name: username },
  });
}

export function ChangeUsernameHeader({ onSuccess }: FunnelComponentProps) {
  return (
    <Flex justifyContent="flex-end" width="100%" padding="16px 24px">
      <Button
        gc-testing-id="btnCloseUsernameChange"
        variant="iconOnly"
        onClick={() => onSuccess({})}
        minWidth={0}
      >
        <CloseIcon color="dune.100" />
      </Button>
    </Flex>
  );
}

export function ChangeUsernameBody({
  onSuccess,
  isModal,
  modalContext,
}: FunnelComponentProps) {
  const { t } = useTranslation();
  const accountData = useAccountData();
  const user = accountData?.user;
  const [newUsername, setNewUsername] = useState("");
  const [validationError, setValidationError] = useState<string | undefined>();
  const [wasUsernameSaved, setWasUsernameSaved] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const isSettings = modalContext === FunnelContextArea.settings;

  const queryClient = useQueryClient();
  const { mutate, isPending } = useMutation({
    mutationFn: postUsername,
    onMutate: () => {
      setValidationError(undefined);
    },
    onSuccess: (data) => {
      setNewUsername("");
      setWasUsernameSaved(true);
      queryClient.setQueryData(GET_ACCOUNT_ME_QUERY_KEY, data);

      // If modalContext is not 'settings', call onSuccess callback
      if (!isSettings) {
        onSuccess({});
      }
    },
    onError: (error) => {
      // Check if it's a validation error (username already exists)
      if (axios.isAxiosError<ServerResponse<unknown>>(error)) {
        const errorData = error.response?.data;
        const fieldsWithErrors = errorData?.payload.form_fields?.filter(
          (field) => field.error !== null,
        );

        if (fieldsWithErrors?.length) {
          setValidationError(fieldsWithErrors[0]?.error);
        } else if (errorData?.error) {
          setValidationError(errorData?.error);
        } else {
          setValidationError(
            t("components.onboarding.changeUsername.errors.validationError"),
          );
        }
      }
    },
  });

  const handleSubmit = () => {
    if (!newUsername.trim() || wasUsernameSaved) {
      onSuccess({});
      return;
    }

    if (inputRef.current) {
      inputRef.current.blur();
    }

    setValidationError(undefined);
    mutate(newUsername);
  };

  if (!user) {
    return null;
  }

  const maxWidth = isModal ? "360px" : "full";
  const isSameUsername = !newUsername.trim() || newUsername === user.name;

  const isButtonDisabled =
    !!(isPending || validationError) ||
    (isSettings && (wasUsernameSaved || isSameUsername));
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
      style={{
        display: "flex",
        flexDirection: "column",
        padding: "24px",
        gap: "16px",
        alignItems: "center",
        width: "100%",
      }}
    >
      <Text
        fontSize="24px"
        fontWeight="700"
        lineHeight="32px"
        letterSpacing="-.5px"
        fontFamily="Bricolage Grotesque 24pt"
        color="neutral.200"
        textAlign="center"
        maxWidth={maxWidth}
      >
        <Trans
          i18nKey="components.onboarding.changeUsername.title"
          components={{ 1: <br /> }}
        />
      </Text>

      <Avatar
        imageSrc={user.avatar_url}
        customSize={119}
        showRing={true}
        ringStroke="5px"
        usePointerEvents={false}
      />

      {/* Username Section */}
      <Flex flexDirection="column" alignItems="center" gap="8px" width="100%">
        <Box width="100%" maxWidth={maxWidth}>
          <TextField
            formRef={inputRef}
            type="text"
            autoFocus={false}
            placeholder={user.name}
            value={newUsername}
            onChange={(value) => {
              setNewUsername(value);
              setValidationError(undefined);
              setWasUsernameSaved(false);
            }}
            prefix={<Text color="dune.600">@</Text>}
            variant="outline"
            height="48px"
            error={validationError}
            errorTextProps={{ justifyContent: "center" }}
            suffix={
              !!(wasUsernameSaved || isSameUsername) ? null : (
                <CloseCircleIcon
                  boxSize="24px"
                  color="dune.600"
                  aria-label="Clear search field"
                  background="inherit"
                  cursor="pointer"
                  pointerEvents="all"
                  borderRightRadius="16px"
                  opacity={!isSameUsername ? 1 : 0}
                  transition="all .1s linear"
                  onClick={() => {
                    setNewUsername("");
                    setValidationError(undefined);
                  }}
                />
              )
            }
          />
        </Box>
        {validationError ? null : (
          <Text
            visibility={wasUsernameSaved ? "visible" : "hidden"}
            fontSize="14px"
            fontWeight="400"
            lineHeight="130%"
            color="success.300"
            textAlign="center"
          >
            {t("components.onboarding.changeUsername.success")}
          </Text>
        )}
      </Flex>

      {/* Public Profile Address Section */}
      <Link
        to={user.url}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          window.open(user.url, "_blank");
        }}
        style={{
          fontSize: "12px",
          fontWeight: "500",
          lineHeight: "150%",
          color: "neutral.100",
          textAlign: "center",
          wordBreak: "break-all",
        }}
      >
        {user.url}
      </Link>

      {/* Continue/Save Button */}
      <Box width="100%" mb="8px" maxWidth={maxWidth}>
        <Button
          type="submit"
          gc-testing-id="btnSaveUsername"
          width="100%"
          size="lg"
          variant="primary"
          isLoading={isPending}
          opacity={isSettings && (wasUsernameSaved || isSameUsername) ? 0.6 : 1}
          isDisabled={isButtonDisabled}
        >
          {t("components.onboarding.changeUsername.saveButton")}
        </Button>
      </Box>
      <Text
        fontSize="12px"
        fontWeight="500"
        lineHeight="150%"
        color="neutral.200"
        textAlign="center"
      >
        <Trans
          i18nKey="components.onboarding.changeUsername.usernameInstruction"
          components={{ 1: <br /> }}
        />
      </Text>
    </form>
  );
}
